/* eslint-disable no-await-in-loop, no-restricted-syntax */
/**
 * Offline layer cache — pre-downloads and looks up the pallet directory
 * the offline frontend needs to resolve a barcode without going to the
 * server. See design §3.3 / §5.3 and plan issues 4.1, 4.3.
 *
 * Lint exemptions: this module uses `for ... of` with sequential
 * `await` inside (writing each layer to a single IDB transaction in
 * sequence; iterating the cache to find a meta-fallback match). The
 * default eslint rules optimise for parallelisable list ops; the
 * sequential pattern is correct for these cases.
 *
 * Two responsibilities:
 *
 *   1. `refresh()` — call `GET /layers/offline-cache/` and replace the
 *      contents of the IndexedDB `offline-layer-cache` store with the
 *      response. Called on app startup and every 6 minutes alongside
 *      the existing config polling cycle (issue 4.3 wires it into
 *      `configHook.tsx`). No-op when `enable_offline_checks` is false.
 *
 *   2. `lookupByBarcode(scanned)` — given a scanned value, return the
 *      matching CachedLayer or null. Exact match on `label` first
 *      (uses the `by-label` IndexedDB index), then a fallback scan
 *      across `meta_scan_values` arrays. The fallback list comes from
 *      the tenant's `barcode_scanner_meta_fallback` config and is
 *      embedded in each cached layer by the backend, so the lookup is
 *      tenant-correct without any client-side knowledge of which keys
 *      to check.
 *
 * The cache is a full overwrite per refresh. We don't merge or reconcile
 * entries — the server's response is the source of truth for "which
 * layers are offline-relevant for this tenant right now". A pallet that
 * dropped out of the offline-cache tabs (e.g. because it left the
 * facility) should disappear from the local cache too.
 */

import { callAvosApi } from "../utils/useAvosApiHook";
import { getOfflineDB, STORE_LAYER_CACHE } from "./db";
import { getOfflineFeatureEnabled } from "./featureFlag";
import type { CachedLayer } from "./types";

interface OfflineCacheResponse {
    layers: CachedLayer[];
}

/**
 * Fetch the offline layer cache from the server and replace the contents
 * of the IndexedDB store. Returns the number of layers cached, or null
 * if the call was skipped (feature flag off) or failed.
 *
 * Failures (network down, server error) are caught and logged. The
 * existing cache is preserved on failure — important for the offline
 * use case: a 6-minute refresh that runs while the device is in a
 * cold-storage room should not wipe last-known data.
 *
 * Called from two places:
 *   - `configHook.tsx` — once per tenant change and once per 6-minute
 *     poll (interval gated on non-config pages).
 *   - `<OfflineServiceWorker />` — once when `enable_offline_checks`
 *     flips true. This second call closes a startup race: configHook's
 *     `[tenant_id]` effect fires this function *before* OfflineServiceWorker
 *     mirrors the flag, so the very first call short-circuits at the
 *     guard below. The interval-driven re-call would catch up — but only
 *     on non-config pages, and only after up to 6 minutes. An operator
 *     who logs in and immediately walks into a cold-storage room would
 *     otherwise have an empty cache. The duplicated call from
 *     OfflineServiceWorker is intentional. See design §5.5.1.
 */
export async function refresh(): Promise<number | null> {
    if (!getOfflineFeatureEnabled()) return null;

    let response: { data: OfflineCacheResponse };
    try {
        response = await callAvosApi("/layers/offline-cache/", {}, { });
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[offline] layer-cache refresh failed; keeping previous data", err);
        return null;
    }

    const layers = Array.isArray(response.data?.layers) ? response.data.layers : [];

    try {
        const db = await getOfflineDB();
        const tx = db.transaction(STORE_LAYER_CACHE, "readwrite");
        await tx.store.clear();
        for (const layer of layers) {
            await tx.store.put(layer);
        }
        await tx.done;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[offline] layer-cache write failed", err);
        return null;
    }

    return layers.length;
}

/**
 * Resolve a scanned barcode against the local cache. Returns the
 * matching CachedLayer or null.
 *
 * Lookup order, mirroring the backend `search_label` endpoint:
 *   1. Exact match on `label` (case-sensitive — the backend behaves the
 *      same way; if you find a real-world barcode that exposes a case
 *      mismatch, fix the cache contents, not the lookup).
 *   2. Fallback: any layer whose `meta_scan_values` array contains the
 *      scanned value. Linear scan; expected to be a few hundred
 *      entries at Bakker scale.
 */
export async function lookupByBarcode(
    scanned: string,
): Promise<CachedLayer | null> {
    if (!scanned) return null;
    if (!getOfflineFeatureEnabled()) return null;

    let db: Awaited<ReturnType<typeof getOfflineDB>>;
    try {
        db = await getOfflineDB();
    } catch {
        return null;
    }

    // Step 1: exact label match via the by-label index.
    try {
        const direct = await db.getFromIndex(STORE_LAYER_CACHE, "by-label", scanned);
        if (direct) return direct;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[offline] layer-cache label lookup failed", err);
    }

    // Step 2: meta-fallback scan.
    try {
        const all = await db.getAll(STORE_LAYER_CACHE);
        for (const layer of all) {
            if (layer.meta_scan_values?.includes(scanned)) {
                return layer;
            }
        }
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[offline] layer-cache meta-fallback scan failed", err);
    }

    return null;
}

/**
 * Direct ID lookup against the offline layer cache. Used by the
 * LayerIndex hydration path when the user is offline and has no
 * currently-active OfflineCheckSession (e.g., they navigated to
 * /layer/<id> after a previous offline submit cleared the session
 * pointer). Without this, LayerIndex would fall back to dispatching
 * `getLayer(id)` against the server, which fails offline and leaves
 * state.layers.current empty — blanking the whole page including the
 * start-check modal. The offline-layer-cache already has the row from
 * the warmer + 6-minute refresh, so we use it.
 *
 * Returns the cached layer or null if the cache doesn't have it (e.g.,
 * a layer that wasn't part of the offline-cache tab's filter set).
 */
export async function lookupById(
    layerId: string,
): Promise<CachedLayer | null> {
    if (!layerId) return null;
    if (!getOfflineFeatureEnabled()) return null;
    try {
        const db = await getOfflineDB();
        const row = await db.get(STORE_LAYER_CACHE, layerId);
        return row ?? null;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[offline] layer-cache id lookup failed", err);
        return null;
    }
}

/**
 * Prefix/substring search over the local layer cache. Mirrors the
 * server's `GET /api/layers/search/?text=...` autocomplete endpoint
 * the LayerAutoComplete uses online — same caller, different data
 * source while offline. Case-insensitive substring match against
 * `label`, plus a fallback against `meta_scan_values`.
 *
 * Returns an empty array (not null) so the caller can pass the result
 * straight to react-select's `loadOptions` without a null-check.
 *
 * Optional `type` filter mirrors the server-side `layer_type` query
 * param: when set, only layers whose `type` matches are returned. The
 * autocomplete passes its `type` prop here so e.g. a "scan pallet"
 * modal won't surface cached subpallets.
 */
export async function searchLayersInCache(
    value: string,
    type?: string | null,
): Promise<CachedLayer[]> {
    if (!getOfflineFeatureEnabled()) return [];

    let db: Awaited<ReturnType<typeof getOfflineDB>>;
    try {
        db = await getOfflineDB();
    } catch {
        return [];
    }

    let all: CachedLayer[];
    try {
        all = await db.getAll(STORE_LAYER_CACHE);
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[offline] layer-cache search failed", err);
        return [];
    }

    const needle = (value ?? "").trim().toLowerCase();
    return all.filter((layer) => {
        if (type && layer.type !== type) return false;
        if (!needle) return true;
        if (layer.label?.toLowerCase().includes(needle)) return true;
        if (layer.meta_scan_values?.some((v) => v.toLowerCase().includes(needle))) return true;
        return false;
    });
}
